import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { initializeApp } from 'firebase/app';
import { getFirestore, collection, addDoc, serverTimestamp, doc, getDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { useAuthState } from 'react-firebase-hooks/auth';
import { v4 as uuidv4 } from 'uuid';
import { Button, Form, Container, Alert, Col, InputGroup, Row } from 'react-bootstrap';
import { FaArrowLeft } from 'react-icons/fa';
import { firebaseConfig } from '../firebase';
import LoginModal from '../auth/LoginModal';  // Import the LoginModal component
import './createAd.css';
import { goods_categories, categoryStructure, conditionOptions, age_categories } from '../menu_options/dropdown_options';
import { cityByProvince } from '../menu_options/location_options';

const app = initializeApp(firebaseConfig);
const firestore = getFirestore(app);
const storage = getStorage(app);
const auth = getAuth(app);

const provinces = [
  'Eastern Cape', 'Free State', 'Gauteng', 'KwaZulu-Natal', 'Limpopo',
  'Mpumalanga', 'North West', 'Northern Cape', 'Western Cape'
];

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 50 }, (_, i) => currentYear - i);

// Image compression function
const compressImage = (file) => {
  return new Promise((resolve, reject) => {
    // Maximum file size in bytes (10MB)
    const maxSize = 20 * 1024 * 1024;
    
    // If file is already small enough, don't compress
    if (file.size <= maxSize) {
      resolve(file);
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        // Preserve original dimensions
        const width = img.width;
        const height = img.height;
        
        // Create canvas and draw image with original dimensions
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Start with higher quality
        let quality = 0.8;
        const compressImage = (q) => {
          canvas.toBlob((blob) => {
            // If the blob is still too large and we can reduce quality further
            if (blob.size > maxSize && q > 0.3) {
              // Reduce quality and try again
              compressImage(q - 0.1);
            } else {
              // Create a new file from the blob
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg', 
                lastModified: new Date().getTime()
              });
              
              console.log(`Compressed ${file.name} from ${file.size} to ${compressedFile.size} bytes, quality: ${q.toFixed(1)}`);
              resolve(compressedFile);
            }
          }, 'image/jpeg', q); // Adjustable quality parameter
        };
        
        // Start compression process
        compressImage(quality);
      };
      img.onerror = (error) => {
        console.error("Error loading image:", error);
        reject(error);
      };
    };
    reader.onerror = (error) => {
      console.error("Error reading file:", error);
      reject(error);
    };
  });
};

const CreateAd = () => {
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const [formData, setFormData] = useState({
    category: '',
    subcategory: '',
    ItemName: '',
    Price: '',
    description: '',
    images: [],
    phonenumber: '',
    province: '',
    city: '',
    condition: '',
    ageRange: '',
    YearModel: '',
    brand: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [compressingImages, setCompressingImages] = useState(false);
  const [subcategories, setSubcategories] = useState([]);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    // Show login modal if user is not logged in
    if (!user) {
      setShowLoginModal(true);
    }
  }, [user]);

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (user) {
        const userRef = doc(firestore, 'users', user.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const userData = userSnap.data();
          setFormData(prevState => ({
            ...prevState,
            phonenumber: userData.phoneNumber || '',
            province: userData.province || '',
          }));
        }
      }
    };
    fetchUserProfile();
  }, [user]);

  // Update subcategories when category changes
  useEffect(() => {
    if (formData.category) {
      setSubcategories(categoryStructure[formData.category] || []);
      // Reset subcategory when category changes
      setFormData(prev => ({...prev, subcategory: ''}));
    } else {
      setSubcategories([]);
    }
  }, [formData.category]);

  // Add useEffect for updating cities when province changes
  useEffect(() => {
    if (formData.province) {
      setCities(cityByProvince[formData.province] || []);
      // Reset city when province changes
      setFormData(prev => ({...prev, city: ''}));
    } else {
      setCities([]);
    }
  }, [formData.province]);

  const formatNumber = (value) => {
    const number = value.replace(/\D/g, '');
    return number.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  };

  const handleChange = async (e) => {
    const { name, value, files } = e.target;
    if (name === 'images') {
      // Clear any previous errors when attempting a new upload
      setError(null);
      
      // Check for maximum 5 images
      if (formData.images.length + files.length > 5) {
        setError("Maximum 5 images allowed per ad");
        return;
      }
      
      // Check file sizes (10MB = 10 * 1024 * 1024 bytes)
      const oversizedFiles = Array.from(files).filter(file => file.size > 10 * 1024 * 1024);
      if (oversizedFiles.length > 0) {
        setError(`Some files exceed the maximum size of 10MB: ${oversizedFiles.map(f => f.name).join(', ')}`);
        return;
      }
      
      setCompressingImages(true);
      try {
        // Create an array of promises for each file compression
        const compressPromises = Array.from(files).map(file => compressImage(file));
        // Wait for all compressions to complete
        const compressedFiles = await Promise.all(compressPromises);
        
        setFormData({ ...formData, images: [...formData.images, ...compressedFiles] });
      } catch (error) {
        console.error("Error compressing images:", error);
        setError("Error compressing images. Please try again.");
      } finally {
        setCompressingImages(false);
      }
    } else if (name === 'Price') {
      const numericValue = value.replace(/\D/g, '');
      const formattedValue = formatNumber(numericValue);
      setFormData({ ...formData, [name]: numericValue });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  // Add function to handle image removal
  const handleRemoveImage = (index) => {
    const updatedImages = [...formData.images];
    updatedImages.splice(index, 1);
    setFormData({ ...formData, images: updatedImages });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      setShowLoginModal(true);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      if (formData.images.length === 0) {
        throw new Error('No images selected');
      }

      const adId = uuidv4();
      const imageUrls = await Promise.all(formData.images.map(async (image, index) => {
        const imageRef = ref(storage, `ads/${adId}_${index}_${image.name}`);
        await uploadBytes(imageRef, image);
        return getDownloadURL(imageRef);
      }));

      await addDoc(collection(firestore, 'ads'), {
        adId,
        category: formData.category,
        subcategory: formData.subcategory,
        ItemName: formData.ItemName,
        Price: Number(formData.Price),
        description: formData.description,
        phonenumber: formData.phonenumber,
        province: formData.province,
        city: formData.city,
        condition: formData.condition,
        ageRange: formData.ageRange,
        YearModel: formData.YearModel ? Number(formData.YearModel) : null,
        brand: formData.brand,
        imageUrls,
        createdAt: serverTimestamp(),
        userId: user.uid,
        userEmail: user.email,
        userName: user.displayName,
        userPhotoURL: user.photoURL
      });

      console.log('Ad created successfully!');
      navigate('/buysell');
    } catch (error) {
      console.error('Error creating ad: ', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginSuccess = () => {
    setShowLoginModal(false);
    // Optionally refresh the page data here if needed
  };

  // If user is not logged in, show container with login modal
  if (!user) {
    return (
      <Container className="mt-5">
        <Button 
          variant="link" 
          className="back-button p-0 mb-3 d-block" 
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          <FaArrowLeft />
        </Button>
        <h2 className="text-center mb-3">Create New Ad</h2>
        <Alert variant="info">
          Please log in to create an ad
        </Alert>
        <LoginModal 
          show={showLoginModal}
          onHide={() => setShowLoginModal(false)}
          onLoginSuccess={handleLoginSuccess}
        />
      </Container>
    );
  }

  return (
    <Container className="mt-5">
      <Button 
        variant="link" 
        className="back-button p-0 mb-3 d-block" 
        onClick={() => navigate(-1)}
        aria-label="Go back"
      >
        <FaArrowLeft />
      </Button>
      <h2 className="text-center mb-4">Create New Ad</h2>
      <Row className="justify-content-center">
        <Col md={10} lg={8} className="submit-form">
          <Form onSubmit={handleSubmit} className="create-ad-form">
            <Form.Group className="mb-3">
              <Form.Label>Category:</Form.Label>
              <Form.Select name="category" value={formData.category} onChange={handleChange} required>
                <option value="">Select Category</option>
                {goods_categories.map((category) => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </Form.Select>
            </Form.Group>
            
            {formData.category && (
              <div className="subcategory-container">
                <Form.Group>
                  <Form.Label>Subcategory:</Form.Label>
                  <Form.Select name="subcategory" value={formData.subcategory} onChange={handleChange} required>
                    <option value="">Select Subcategory</option>
                    {subcategories.map((subcategory) => (
                      <option key={subcategory} value={subcategory}>{subcategory}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Brand:</Form.Label>
              <Form.Control 
                type="text" 
                name="brand" 
                value={formData.brand} 
                onChange={handleChange} 
                placeholder="Enter brand name"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Item Name:</Form.Label>
              <Form.Control type="text" name="ItemName" value={formData.ItemName} onChange={handleChange} required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Age Range:</Form.Label>
              <Form.Select name="ageRange" value={formData.ageRange} onChange={handleChange} required>
                <option value="">Select Age Range</option>
                {age_categories.map((age) => (
                  <option key={age} value={age}>{age}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Price:</Form.Label>
              <InputGroup>
                <InputGroup.Text>R</InputGroup.Text>
                <Form.Control
                  type="text"
                  name="Price"
                  value={formatNumber(formData.Price)}
                  onChange={handleChange}
                  required
                  className="price-input"
                />
              </InputGroup>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description:</Form.Label>
              <Form.Control as="textarea" name="description" value={formData.description} onChange={handleChange} required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Images:</Form.Label>
              <Form.Control 
                type="file" 
                name="images" 
                accept="image/*" 
                onChange={handleChange} 
                multiple 
                required={formData.images.length === 0}
                disabled={compressingImages}
              />
              {compressingImages && <p className="text-info mt-1">Compressing images...</p>}
              <small className="text-muted">
                Maximum 5 images allowed, each image must be less than 10MB. 
                Images will be compressed to reduce file size while preserving original dimensions.
                Selected: {formData.images.length}/5
              </small>
            </Form.Group>

            {/* Add image preview section */}
            {formData.images.length > 0 && (
              <Row className="image-preview-row mb-3">
                {formData.images.map((image, index) => (
                  <Col key={index} xs={6} md={4} lg={3} className="mb-3">
                    <div className="image-preview-container" style={{ position: 'relative' }}>
                      <img 
                        src={URL.createObjectURL(image)} 
                        alt={`Preview ${index + 1}`} 
                        style={{ 
                          width: '100%', 
                          height: '150px', 
                          objectFit: 'cover', 
                          borderRadius: '4px' 
                        }} 
                      />
                      <Button
                        variant="danger"
                        size="sm"
                        style={{
                          position: 'absolute',
                          top: '5px',
                          right: '5px',
                          padding: '0.1rem 0.5rem',
                          fontSize: '0.8rem'
                        }}
                        onClick={() => handleRemoveImage(index)}
                      >
                        X
                      </Button>
                    </div>
                  </Col>
                ))}
              </Row>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Condition:</Form.Label>
              <Form.Select name="condition" value={formData.condition} onChange={handleChange} required>
                <option value="">Select Condition</option>
                {conditionOptions.map((condition) => (
                  <option key={condition} value={condition}>{condition}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <div className='personal-section'>
              <h5>Personal Information</h5>
            </div>
            <Form.Group className="mb-3">
              <Form.Label>Phone Number</Form.Label>
              <Form.Control type="text" name="phonenumber" value={formData.phonenumber} onChange={handleChange} required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Province</Form.Label>
              <Form.Select name="province" value={formData.province} onChange={handleChange} required>
                <option value="">Select Province</option>
                {provinces.map((province) => (
                  <option key={province} value={province}>{province}</option>
                ))}
              </Form.Select>
            </Form.Group>
            {formData.province && (
              <Form.Group className="mb-3">
                <Form.Label>City</Form.Label>
                <Form.Select name="city" value={formData.city} onChange={handleChange} required>
                  <option value="">Select City</option>
                  {cities.map((city) => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}
            {error && <Alert variant="danger">{error}</Alert>}
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Creating...' : 'Create Ad'}
            </Button>
          </Form>
        </Col>
      </Row>
      
      {/* Login Modal */}
      <LoginModal 
        show={showLoginModal}
        onHide={() => setShowLoginModal(false)}
        onLoginSuccess={handleLoginSuccess}
      />
    </Container>
  );
};

export default CreateAd;